import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRepMode } from "./RepModeContext";
import { fetchRepHome, logRepContact } from "./repServices";
import { activityTone, athleteSubtitle, profileLabel } from "./repUtils";
import { EMPTY_VALUE, initialsOf, mailtoFor } from "../../ForCoaches/v2/coachUtils";
import { greeting } from "../../ForBrands/brandDealUtils";
import { Avatar, Badge, Btn, Card, ErrorState, InsightBox, Loading, MetricCard, Overline, PageHeader } from "../../ForBrands/ui/BrandUI";

// Rep Mode · Home (Figma REP MODE V2 · 01 Home): greeting, KPI strip, the
// attention queue across the athletes who linked this rep, the athletes
// table with deal activity and profile state, roster analysis and the
// "What you can see" card. Offer amounts and payouts stay private to the
// athlete unless they share financial terms with the rep.

const ATTENTION_TONES = { critical: "critical", attention: "attention", dark: "dark", passive: "passive", outline: "outline" };

const VISIBLE = [
  { ok: true, text: "Deal status and timeline for each athlete" },
  { ok: true, text: "Compliance and exclusivity conflicts" },
  { ok: true, text: "Guardian and rep linkage" },
  { ok: false, text: "Offer amounts and payout values" },
  { ok: false, text: "Direct messages between athlete and brand" },
];

const AttentionRow = ({ item, onAct, busy }) => (
  <div className="flex items-center justify-between gap-4 border-b border-bm-mutedbg py-[14px] last:border-b-0">
    <div className="flex min-w-0 items-center gap-3">
      <Badge tone={ATTENTION_TONES[item.tone] || "outline"} className="shrink-0">
        {item.badge}
      </Badge>
      <div className="flex min-w-0 flex-col gap-[2px]">
        <div className="bm-h-xs text-bm-ink">{item.title}</div>
        <div className="bm-body-m text-bm-muted">{item.meta}</div>
      </div>
    </div>
    <Btn variant={item.tone === "critical" || item.tone === "attention" ? "primary" : "secondary"} size="sm" onClick={() => onAct(item)} disabled={busy === item.title}>
      {busy === item.title ? "Opening..." : item.action}
    </Btn>
  </div>
);

const CheckIcon = ({ ok }) =>
  ok ? (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1f9d55]">
      <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
        <path d="M1 4.5l3 3L10 1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ) : (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bm-mutedbg">
      <span className="h-[2px] w-[9px] rounded-full bg-bm-tertiary" />
    </span>
  );

const RepHomePage = () => {
  const navigate = useNavigate();
  const { refreshBadges, openShare } = useRepMode();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const payload = await fetchRepHome();
      setData(payload);
      setError(null);
    } catch (err) {
      setError(err.message || "Could not load your home view");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const act = async (item) => {
    if (item.href) {
      navigate(item.href);
      return;
    }
    const row = (data?.rows || []).find((r) => item.athleteIds?.includes(r.athleteId));
    const email = item.athleteEmail || row?.email;
    if (!email) {
      toast.error("This athlete has no email on file.");
      return;
    }
    setBusy(item.title);
    try {
      const kind = item.kind === "guardian" ? "request_link" : "notify";
      await logRepContact({ email, name: row ? `${row.name} ${row.surname}` : "", kind, athleteEmail: email });
      window.location.href = mailtoFor({
        email,
        subject: item.kind === "guardian" ? "Please add your parent or guardian to your CACHE Circle" : item.title,
        body: item.kind === "guardian" ? "Hi,\n\nYour deal cannot be finalized until a parent or guardian is linked. Please open My Circle in CACHE and add them.\n\n" : `${item.meta}\n\n`,
      });
      refreshBadges();
    } catch (err) {
      toast.error(err.message || "Could not log the reminder");
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <div className="py-10">
        <ErrorState>{error}</ErrorState>
      </div>
    );
  }
  if (!data) return <Loading />;

  const { rep, kpis, attention, rows, analysis } = data;
  const n = kpis.athletes || 0;

  return (
    <div>
      <PageHeader
        eyebrow="Rep Mode"
        title={`${greeting()}, ${rep?.firstName || "there"}`}
        subtitle={
          n
            ? `${kpis.attention} thing${kpis.attention === 1 ? "" : "s"} need${kpis.attention === 1 ? "s" : ""} you across ${n} athlete${n === 1 ? "" : "s"} you represent.`
            : "No athletes have linked you yet. Share your Rep ID and they can add you from My Circle."
        }
        actions={<Btn onClick={openShare}>Share your Rep ID</Btn>}
      />

      <div className="flex flex-col gap-4 pt-2">
        <div className="flex flex-wrap gap-3">
          <MetricCard muted label="Needs your attention" value={kpis.attention} sub="actions waiting on you" />
          <MetricCard label="Athletes" value={n} sub={`${kpis.athletesWithDeals} with deals`} />
          <MetricCard label="Deals in progress" value={kpis.dealsInProgress} sub={`across ${kpis.brands} brand${kpis.brands === 1 ? "" : "s"}`} />
          <MetricCard label="Active deals" value={kpis.activeDeals} sub="signed and running" />
          <MetricCard label="Compliance flags" value={kpis.complianceFlags} sub={kpis.complianceFlags === 1 ? "needs review" : "need review"} />
        </div>

        <Card padding="p-[22px]" className="flex flex-col gap-[6px]" id="attention">
          <div className="flex items-center justify-between pb-[6px]">
            <Overline>Needs your attention</Overline>
            <span className="bm-body-s text-bm-tertiary">Financial terms are private to the athlete</span>
          </div>
          {attention.length ? (
            <div>
              {attention.map((item, i) => (
                <AttentionRow key={`${item.kind}-${i}`} item={item} onAct={act} busy={busy} />
              ))}
            </div>
          ) : (
            <p className="py-6 text-center bm-body-m text-bm-muted">Nothing needs you right now. Offers, contracts, deadlines and clashes across your athletes land here.</p>
          )}
        </Card>

        <Card padding="p-0" className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px]">
              <thead>
                <tr className="bg-bm-subtle">
                  <th className="bm-th pl-4 text-left">Athlete</th>
                  <th className="bm-th text-left">Sport</th>
                  <th className="bm-th text-left">CacheScore</th>
                  <th className="bm-th text-left">Deal activity</th>
                  <th className="bm-th text-left">Profile</th>
                  <th className="bm-th pr-4 text-right" />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center bm-body-m text-bm-muted">
                      Athletes who add your Rep ID in My Circle appear here.
                    </td>
                  </tr>
                )}
                {rows.map((r) => {
                  const profile = profileLabel(r);
                  return (
                    <tr key={r.athleteId} className="border-t border-bm-border">
                      <td className="bm-td pl-4">
                        <span className="flex items-center gap-3">
                          <Avatar src={r.avatar} initials={initialsOf(r)} size={36} tone="outline" />
                          <span className="flex min-w-0 flex-col">
                            <span className="bm-h-xs text-bm-ink">
                              {r.name} {r.surname}
                            </span>
                            <span className="bm-body-s text-bm-muted">{athleteSubtitle(r) || EMPTY_VALUE}</span>
                          </span>
                        </span>
                      </td>
                      <td className="bm-td bm-body-m text-bm-ink">{r.sport || EMPTY_VALUE}</td>
                      <td className="bm-td bm-num-m text-bm-ink">{r.score ?? EMPTY_VALUE}</td>
                      <td className="bm-td">
                        <Badge tone={activityTone(r)}>{r.activity}</Badge>
                      </td>
                      <td className={`bm-td bm-body-m ${profile.tone === "attention" ? "text-bm-critical" : profile.tone === "ink" ? "text-bm-ink" : "text-bm-muted"}`}>{profile.text}</td>
                      <td className="bm-td pr-4 text-right">
                        <Btn variant="secondary" size="sm" onClick={() => navigate(`/business/athlette/profile/${r.athleteId}`)}>
                          View
                        </Btn>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <InsightBox
            className="min-w-0 flex-1"
            title={`Roster analysis · ${analysis.total ?? 0} athlete${analysis.total === 1 ? "" : "s"}`}
            tiles={[
              { label: "Athletes with deals", value: `${analysis.athletesWithDeals ?? 0} of ${analysis.total ?? 0}`, sub: analysis.total ? `${Math.round((100 * (analysis.athletesWithDeals || 0)) / analysis.total)}% of roster` : "no athletes yet" },
              { label: "Avg. CacheScore", value: analysis.avgScore ?? EMPTY_VALUE, sub: analysis.avgScoreDelta != null ? `${analysis.avgScoreDelta >= 0 ? "+" : ""}${analysis.avgScoreDelta} in 30d` : "no trend yet" },
              { label: "Profiles complete", value: `${analysis.profilesComplete ?? 0} of ${analysis.total ?? 0}`, sub: `${Math.max(0, (analysis.total ?? 0) - (analysis.profilesComplete ?? 0))} need attention` },
            ]}
          >
            {analysis.text}
          </InsightBox>
          <Card padding="p-6" className="flex w-full flex-col gap-4 lg:w-[420px]">
            <Overline>What you can see</Overline>
            <ul className="flex flex-col gap-3">
              {VISIBLE.map((v) => (
                <li key={v.text} className="flex items-center gap-3">
                  <CheckIcon ok={v.ok} />
                  <span className={`bm-body-m ${v.ok ? "text-bm-ink" : "text-bm-tertiary"}`}>{v.text}</span>
                </li>
              ))}
            </ul>
            <p className="bm-body-s text-bm-tertiary">Financial terms stay between the athlete and the brand unless the athlete shares them with you.</p>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RepHomePage;
